import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { requestMobile } from '@/lib/request'
import { toast } from 'sonner'

interface BankInfo {
  id: string
  bankName: string
  bankCode: string
}

interface BindCardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountInfo: {
    plat: string
    remarks_name: string
  }
  onSuccess?: () => void
}

export function BindCardDialog({ open, onOpenChange, accountInfo, onSuccess }: BindCardDialogProps) {
  const [loading, setLoading] = useState(false)
  const [bankList, setBankList] = useState<BankInfo[]>([])
  const [formData, setFormData] = useState({
    real_name: '',
    bank_account: '',
    bank_address: '',
    bank_id: ''
  })

  // 当对话框打开时加载银行列表
  useEffect(() => {
    if (open) {
      loadBankList()
    } else {
      resetDialog()
    }
  }, [open])

  const loadBankList = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/bank-list', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })

      if (resp && resp.data && resp.data.length > 0) {
        setBankList(resp.data)
      } else {
        toast.error('没有可用的银行')
        onOpenChange(false)
      }
    } catch (error) {
      console.error('Failed to load bank list:', error)
      toast.error('加载银行列表失败')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.real_name || !formData.bank_account || !formData.bank_address || !formData.bank_id) {
      toast.error('请填写完整信息')
      return
    }

    const selectedBank = bankList.find(b => b.id === formData.bank_id)
    if (!selectedBank) {
      toast.error('请选择银行')
      return
    }

    setLoading(true)
    try {
      const param = {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        real_name: formData.real_name,
        bank_account: formData.bank_account,
        bank_address: formData.bank_address,
        bank_code: selectedBank.bankCode,
        bank_id: selectedBank.id,
        bank_name: selectedBank.bankName,
        type: 1
      }

      const resp = await requestMobile('/common/apis/v1/bind-card', param)

      if (resp && resp.code === 0) {
        toast.success('绑卡成功')
        onOpenChange(false)
        resetDialog()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Failed to bind card:', error)
      toast.error('绑卡失败')
    } finally {
      setLoading(false)
    }
  }

  const resetDialog = () => {
    setFormData({
      real_name: '',
      bank_account: '',
      bank_address: '',
      bank_id: ''
    })
    setBankList([])
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>绑定银行卡</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 姓名 */}
          <div className="space-y-2">
            <Label htmlFor="real_name">姓名</Label>
            <Input
              id="real_name"
              placeholder="请输入姓名"
              value={formData.real_name}
              onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
              required
            />
          </div>

          {/* 银行卡号 */}
          <div className="space-y-2">
            <Label htmlFor="bank_account">银行卡号</Label>
            <Input
              id="bank_account"
              placeholder="请输入银行卡号"
              value={formData.bank_account}
              onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
              required
            />
          </div>

          {/* 开户行地址 */}
          <div className="space-y-2">
            <Label htmlFor="bank_address">开户行地址</Label>
            <Input
              id="bank_address"
              placeholder="请输入开户行地址"
              value={formData.bank_address}
              onChange={(e) => setFormData({ ...formData, bank_address: e.target.value })}
              required
            />
          </div>

          {/* 选择银行 */}
          <div className="space-y-2">
            <Label htmlFor="bank">选择银行</Label>
            <Select value={formData.bank_id} onValueChange={(value) => setFormData({ ...formData, bank_id: value })}>
              <SelectTrigger id="bank">
                <SelectValue placeholder="请选择银行" />
              </SelectTrigger>
              <SelectContent>
                {bankList.map((bank) => (
                  <SelectItem key={bank.id} value={bank.id}>
                    {bank.bankName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              取消
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? '提交中...' : '提交'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
